/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {
    VisibilityIcon,
    VisibilityOffIcon
} from '@salesforce/retail-react-app/app/components/icons'
import {
    Checkbox,
    FormControl,
    FormErrorMessage,
    FormLabel,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    Select
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import React, {useState} from 'react'
import {Controller} from 'react-hook-form'

import {colors} from '../../constants'
import {RoundDotIcon} from '../icons'

const Field = ({
    name,
    label,
    formLabel,
    type = 'text',
    options = [],
    rules = {},
    error,
    placeholder,
    inputProps,
    control,
    defaultValue,
    helpText,
    passwordHideIcon = true,
    children,
    checkboxStyles
}) => {
    const [hidePassword, setHidePassword] = useState(true)
    const PasswordIcon = hidePassword ? VisibilityIcon : VisibilityOffIcon
    const inputType =
        type === 'password' && hidePassword
            ? 'password'
            : type === 'password'
            ? 'text'
            : type

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            defaultValue={defaultValue}
            render={({field: {onChange, value, ref}}) => {
                const _inputProps =
                    typeof inputProps === 'function'
                        ? inputProps({value, onChange})
                        : inputProps

                return (
                    <FormControl id={name} isInvalid={error}>
                        {!['checkbox', 'radio'].includes(type) &&
                            type !== 'hidden' &&
                            (formLabel || (
                                <FormLabel
                                    fontFamily="Roboto"
                                    fontWeight="300"
                                    fontSize="14px"
                                    color={colors.paleYellow}
                                >
                                    {label}
                                </FormLabel>
                            ))}

                        <InputGroup>
                            {[
                                'text',
                                'password',
                                'email',
                                'phone',
                                'tel',
                                'number'
                            ].includes(type) && (
                                <Input
                                    ref={ref}
                                    onChange={onChange}
                                    value={value}
                                    type={inputType}
                                    placeholder={placeholder}
                                    {..._inputProps}
                                    border="1px solid"
                                    borderColor={colors.textLight}
                                    borderRadius="20px"
                                    fontSize="16px"
                                    _hover={{
                                        borderColor: colors.black
                                    }}
                                    _placeholder={{
                                        color: colors.black,
                                        fontSize: '14px',
                                        fontWeight: '300',
                                        fontFamily: 'Roboto'
                                    }}
                                    height="40px"
                                />
                            )}

                            {type === 'hidden' && (
                                <input
                                    ref={ref}
                                    onChange={onChange}
                                    value={value}
                                    type="hidden"
                                    {..._inputProps}
                                />
                            )}

                            {type === 'password' && passwordHideIcon && (
                                <InputRightElement>
                                    <IconButton
                                        variant="ghosted"
                                        aria-label="Show password"
                                        icon={
                                            <PasswordIcon
                                                color="gray.500"
                                                boxSize={6}
                                                size={4}
                                            />
                                        }
                                        onClick={() =>
                                            setHidePassword(!hidePassword)
                                        }
                                    />
                                </InputRightElement>
                            )}

                            {type === 'select' && (
                                <Select
                                    ref={ref}
                                    onChange={onChange}
                                    value={value}
                                    placeholder={placeholder}
                                    color={colors.black}
                                    fontSize="14px"
                                    fontFamily="Roboto"
                                    fontWeight="300"
                                    {..._inputProps}
                                    border="1px solid"
                                    borderColor={colors.black}
                                    borderRadius="20px"
                                    _hover={{
                                        borderColor: colors.black
                                    }}
                                    _placeholder={{
                                        color: colors.black,
                                        fontSize: '14px',
                                        fontWeight: '300',
                                        fontFamily: 'Roboto'
                                    }}
                                >
                                    {options.map((opt) => (
                                        <option
                                            key={`${opt.label}-${opt.value}`}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </option>
                                    ))}
                                </Select>
                            )}

                            {type === 'date' && (
                                <Input
                                    ref={ref}
                                    onChange={onChange}
                                    value={value}
                                    type="text"
                                    placeholder={placeholder}
                                    {..._inputProps}
                                    border="1px solid"
                                    borderColor={colors.black}
                                    borderRadius="20px"
                                    fontSize="16px"
                                    _hover={{
                                        borderColor: colors.black
                                    }}
                                    onFocus={(e) => (e.target.type = 'date')}
                                    onBlur={(e) => (e.target.type = 'text')}
                                    _placeholder={{
                                        color: colors.black,
                                        fontSize: '14px',
                                        fontWeight: '300',
                                        fontFamily: 'Roboto'
                                    }}
                                />
                            )}

                            {type === 'checkbox' && (
                                <Checkbox
                                    ref={ref}
                                    onChange={(e) => onChange(e.target.checked)}
                                    isChecked={value}
                                    {..._inputProps}
                                    iconColor="white" // Adjust the color of the dot as needed
                                    iconSize="14px" // Adjust the size of the dot as needed
                                    icon={<RoundDotIcon boxSize="10px" />}
                                    sx={{
                                        '.chakra-checkbox__control': {
                                            borderRadius: 'full', // Apply rounded corners
                                            _checkedAndChild: {
                                                transform: 'translateY(0.1em)'
                                            },
                                            border: '1px solid',
                                            borderColor: 'black !important',
                                            backgroundColor: 'white !important',
                                            background: 'white !important',
                                            outline: 'none',
                                            _hover: {
                                                backgroundColor:
                                                    'white !important'
                                            },
                                            ...checkboxStyles
                                        }
                                    }}
                                    _placeholder={{
                                        color: colors.black,
                                        fontSize: '14px',
                                        fontWeight: '300',
                                        fontFamily: 'Roboto'
                                    }}
                                >
                                    {formLabel || label}
                                </Checkbox>
                            )}

                            {children}
                        </InputGroup>

                        {error && !type !== 'hidden' && (
                            <FormErrorMessage color="red.600">
                                {error.message}
                            </FormErrorMessage>
                        )}

                        {helpText}
                    </FormControl>
                )
            }}
        />
    )
}

Field.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    formLabel: PropTypes.any,
    type: PropTypes.oneOf([
        'text',
        'number',
        'password',
        'email',
        'phone',
        'tel',
        'select',
        'checkbox',
        'hidden'
    ]),
    options: PropTypes.arrayOf(
        PropTypes.shape({label: PropTypes.string, value: PropTypes.any})
    ),
    rules: PropTypes.object,
    error: PropTypes.shape({message: PropTypes.string}),
    placeholder: PropTypes.string,
    inputProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    control: PropTypes.object,
    defaultValue: PropTypes.any,
    helpText: PropTypes.any,
    passwordHideIcon: PropTypes.bool,
    children: PropTypes.any,
    checkboxStyles: PropTypes.object
}

export default Field
